import React, {useEffect} from "react";
import {observer} from "mobx-react-lite";
import {useSitStores, useStores} from "~/stores";
import {_toCurrencyWithP, findAbbreviationOfGroup} from "~/components/currency-component/CurrencyClasses.ts";
import {MithraEchart} from "~/services/echarts/MithraEchart";
import type {EChartContextType} from "~/services/echarts/EChartProvider";
import type {EChartsType} from "echarts/types/dist/shared";
import {FinancialFilters} from "../financial-dashboard/financialFilters";
import {complexityTierLabel, stageGateLabel, workstreamLabel} from "../../sitLabels";
import {DOI_N_COLOR} from "../../components/design/SitDesignAtoms";
import {CircularProgress} from "@mui/material";
import i18n from "../../../i18n/i18n";

const dash = (k: string) => i18n.t(`dash.${k}`, {ns: 'sit', defaultValue: k});
const n = (v: number | null | undefined) => Number(v) || 0;

// Chart palette (Mithra 3.0 indicators).
const C_TOTAL = 'rgb(31,122,71)';
const C_DELTA = 'rgb(122,127,138)';
const C_RED = 'rgb(244,67,54)';
const C_GRID = 'rgba(20,28,60,.08)';
const C_AXIS = 'rgba(20,28,60,.16)';
// Complexity tiers: 1 Complex / 2 Strategic / 3 Quick win — hard→easy = red→green.
const TIER_COLORS: Record<number, string> = {1: 'rgb(229,72,77)', 2: 'rgb(242,169,59)', 3: 'rgb(34,158,92)'};
// Shared text styling for chart labels/axes (matches the design's Inter font stack).
const FONT = 'Inter, Roboto, sans-serif';

// ECharts computes its hover/emphasis highlight by lightening a series' base
// color — that color math can't parse CSS `var(--ac-*)` strings (only literal
// rgb()/hex), so a bar styled with e.g. `var(--ac-navy)` renders invisible on
// hover instead of highlighted. Mirror the sitDesign.css `--ac-*` tokens as
// literal rgb() here for anything fed into an ECharts option (keep in sync by
// hand — these design tokens rarely change). Plain HTML/SVG elements outside
// ECharts (e.g. the ChartCard legend dots) are unaffected and can keep var().
const AC_NAVY = 'rgb(20, 28, 60)';
const AC_ACCENT = 'rgb(45, 111, 245)';
const AC_LINE = 'rgb(231, 233, 238)';
const AC_BG_ROW = 'rgb(248, 249, 251)';
const AC_BG = 'rgb(252, 252, 253)';
const AC_INK = 'rgb(28, 30, 36)';
const AC_INK_2 = 'rgb(60, 64, 74)';
const AC_FAINT = 'rgb(168, 172, 182)';

type LegendItem = {color: string; label: string};

const ChartCard: React.FC<{
    title: string; sub?: string; legend?: LegendItem[]; span?: boolean; children: React.ReactNode;
}> = ({title, sub, legend, span, children}) => (
    <div className={'sit-card' + (span ? ' span2' : '')}>
        <div className="sit-cardhead">
            <h3 className="sit-cardtitle">{title}</h3>
            {sub && <span className="sit-cardsub">{sub}</span>}
            {legend && <div className="sit-legend sm">
                {legend.map((l, i) =>
                    <span className="sit-legend-item" key={i}>
                        <span className="sit-legend-dot" style={{background: l.color}}/>{l.label}
                    </span>)}
            </div>}
        </div>
        {children}
    </div>
);

type BarRow = {label: string; value: number};

// ── Horizontal bars (workstream / stage gate / funnel) — shared ECharts builder ──

type HBarRow = {
    label: string;
    value: number;
    color?: string;
    badge?: {text: string; color: string};
    secondary?: string;
};
type HBarData = {rows: HBarRow[]; fmt: (v: number) => string; labelWidth: number; wrap?: boolean};

function buildHBarOption({rows, fmt, labelWidth, wrap}: HBarData) {
    const max = Math.max(...rows.map(r => r.value), 1);
    const rich: Record<string, object> = {};
    rows.forEach((r, i) => {
        if (r.badge) rich[`b${i}`] = {
            color: '#fff',
            backgroundColor: r.badge.color,
            borderRadius: 6,
            padding: [3, 6, 2],
            fontWeight: 700,
            fontSize: 11,
        };
    });
    return {
        tooltip: {
            trigger: 'item',
            formatter: (p: any) => `${rows[p.dataIndex].label}: ${fmt(rows[p.dataIndex].value)}`,
        },
        grid: {left: labelWidth, right: 76, top: 4, bottom: 4},
        xAxis: {type: 'value', max, show: false},
        yAxis: {
            type: 'category',
            inverse: true,
            data: rows.map(r => r.label),
            axisLine: {show: false},
            axisTick: {show: false},
            axisLabel: {
                width: labelWidth - 14,
                overflow: wrap ? 'break' : 'truncate',
                lineHeight: 14,
                color: AC_INK_2,
                fontSize: 12,
                fontFamily: FONT,
                rich,
                formatter: (val: string, idx: number) => {
                    const r = rows[idx];
                    return r.badge ? `{b${idx}|${r.badge.text}}  ${val}` : val;
                },
            },
        },
        series: [{
            type: 'bar',
            data: rows.map(r => ({value: r.value, itemStyle: {color: r.color ?? AC_NAVY, borderRadius: 2}})),
            barWidth: 12,
            showBackground: true,
            backgroundStyle: {color: AC_BG_ROW, borderRadius: 2},
            label: {
                show: true,
                position: 'right',
                color: AC_INK,
                fontWeight: 600,
                fontSize: 12,
                fontFamily: FONT,
                formatter: (p: any) => {
                    const r = rows[p.dataIndex];
                    return r.secondary ? `${fmt(r.value)}  ${r.secondary}` : fmt(r.value);
                },
            },
        }],
    };
}

function initHBar(echart: EChartContextType, root: HTMLElement, data: any[]): EChartsType {
    const d = data[0] as HBarData;
    const chart = echart.init(root, 'mithra', {renderer: 'svg', height: Math.max(d.rows.length * 38, 64)});
    chart.setOption(buildHBarOption(d));
    return chart;
}

function updateHBar(chart: EChartsType, data: any[]) {
    chart.setOption(buildHBarOption(data[0] as HBarData), {notMerge: true});
}

const HBarChart: React.FC<HBarData> = (props) => {
    if (props.rows.length === 0) return <div className="sit-muted">{dash('noData')}</div>;
    return <MithraEchart initChart={initHBar} updateChart={updateHBar} data={[props]}/>;
};

/** Money horizontal bars (workstream). */
const MoneyBars: React.FC<{rows: BarRow[]; fmt: (v: number) => string}> = ({rows, fmt}) =>
    <HBarChart rows={rows} fmt={fmt} labelWidth={132}/>;

/** Count bars with an N-badge (by stage gate) — badge + bar share the DOI-N colour. */
const StageGateBars: React.FC<{rows: {n: string; label: string; value: number}[]}> = ({rows}) =>
    <HBarChart
        rows={rows.map(r => ({
            label: r.label, value: r.value,
            badge: {text: r.n, color: DOI_N_COLOR[r.n]},
            color: DOI_N_COLOR[r.n],
        }))}
        fmt={(v) => v.toLocaleString()}
        labelWidth={166}
        wrap
    />;

/** Capture funnel: horizontal bars + step conversion %. */
const Funnel: React.FC<{rows: BarRow[]; fmt: (v: number) => string}> = ({rows, fmt}) =>
    <HBarChart
        rows={rows.map((r, i) => {
            const prev = i > 0 ? rows[i - 1].value : 0;
            const conv = i > 0 && prev ? Math.round(r.value / prev * 100) : null;
            return {label: r.label, value: r.value, secondary: conv !== null ? `↓ ${conv}%` : undefined};
        })}
        fmt={fmt}
        labelWidth={150}
    />;

// ── By year: grouped vertical bars (target vs saving), years on the x-axis ──

type YearBarsData = {rows: {year: number; total: number; target: number}[]; fmt: (v: number) => string};

function buildYearBarsOption({rows, fmt}: YearBarsData) {
    return {
        tooltip: {trigger: 'axis', valueFormatter: (v: any) => fmt(Number(v))},
        grid: {left: 8, right: 10, top: 18, bottom: 30, containLabel: true},
        xAxis: {
            type: 'category',
            data: rows.map(r => String(r.year)),
            axisLine: {lineStyle: {color: C_AXIS}},
            axisTick: {show: false},
            axisLabel: {color: AC_INK_2, fontSize: 12, fontFamily: FONT},
        },
        yAxis: {
            type: 'value',
            splitLine: {lineStyle: {color: C_GRID}},
            axisLabel: {formatter: fmt, color: AC_FAINT, fontSize: 11, fontFamily: FONT},
            axisLine: {show: false},
            axisTick: {show: false},
        },
        series: [
            {
                name: dash('target'), type: 'bar',
                data: rows.map(r => r.target),
                barMaxWidth: 26,
                itemStyle: {color: C_RED, borderRadius: [2, 2, 0, 0]},
            },
            {
                name: dash('total'), type: 'bar',
                data: rows.map(r => r.total),
                barMaxWidth: 26,
                itemStyle: {color: AC_NAVY, borderRadius: [2, 2, 0, 0]},
            },
        ],
    };
}

function initYearBars(echart: EChartContextType, root: HTMLElement, data: any[]): EChartsType {
    const chart = echart.init(root, 'mithra', {renderer: 'svg', height: 220});
    chart.setOption(buildYearBarsOption(data[0] as YearBarsData));
    return chart;
}

function updateYearBars(chart: EChartsType, data: any[]) {
    chart.setOption(buildYearBarsOption(data[0] as YearBarsData), {notMerge: true});
}

const YearBars: React.FC<YearBarsData> = (props) => {
    if (props.rows.length === 0) return <div className="sit-muted">{dash('noData')}</div>;
    return <MithraEchart initChart={initYearBars} updateChart={updateYearBars} data={[props]}/>;
};

// ── Complexity donut: initiative counts per tier, with center total + legend ──

type DonutData = {rows: {label: string; value: number; color: string}[]; total: number};

function buildDonutOption({rows, total}: DonutData) {
    return {
        tooltip: {
            trigger: 'item',
            formatter: (p: any) => `${p.name}: ${p.value} (${total ? Math.round(p.value / total * 100) : 0}%)`,
        },
        graphic: {
            elements: total > 0 ? [
                {
                    type: 'text', left: 'center', top: '42%', style: {
                        text: String(total), fontSize: 24, fontWeight: 700,
                        fill: AC_INK, fontFamily: FONT, align: 'center',
                    },
                },
                {
                    type: 'text', left: 'center', top: '58%', style: {
                        text: dash('initiatives'), fontSize: 11,
                        fill: AC_FAINT, fontFamily: FONT, align: 'center',
                    },
                },
            ] : [],
        },
        series: [{
            type: 'pie',
            radius: ['60%', '86%'],
            center: ['50%', '50%'],
            avoidLabelOverlap: false,
            label: {show: false},
            labelLine: {show: false},
            itemStyle: {borderColor: AC_BG, borderWidth: total > 0 ? 2 : 0},
            data: total > 0 ? rows.map(r => ({name: r.label, value: r.value, itemStyle: {color: r.color}})) : [],
        }],
    };
}

function initDonut(echart: EChartContextType, root: HTMLElement, data: any[]): EChartsType {
    const chart = echart.init(root, 'mithra', {renderer: 'svg', width: 200, height: 200});
    chart.setOption(buildDonutOption(data[0] as DonutData));
    return chart;
}

function updateDonut(chart: EChartsType, data: any[]) {
    chart.setOption(buildDonutOption(data[0] as DonutData), {notMerge: true});
}

const ComplexityDonut: React.FC<{rows: {label: string; value: number; color: string}[]}> = ({rows}) => {
    const total = rows.reduce((s, r) => s + r.value, 0);
    return (
        <div className="sit-donut-wrap">
            <MithraEchart initChart={initDonut} updateChart={updateDonut} data={[{rows, total}]}/>
            <div className="sit-donut-legend">
                {rows.map((d) => (
                    <div className="sit-dleg-row" key={d.label}>
                        <span className="sit-dleg-dot" style={{background: d.color}}/>
                        <span className="sit-dleg-label">{d.label}</span>
                        <span className="sit-dleg-val">{d.value}</span>
                        <span className="sit-dleg-pct">{total ? Math.round(d.value / total * 100) : 0}%</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

// ── Semicircular gauge — % of the NExO target reached ──

type GaugeData = {pct: number};

function buildGaugeOption({pct}: GaugeData) {
    return {
        series: [{
            type: 'gauge',
            startAngle: 180,
            endAngle: 0,
            min: 0,
            max: 100,
            center: ['50%', '85%'],
            radius: '135%',
            progress: {show: true, width: 15, itemStyle: {color: AC_NAVY}},
            axisLine: {lineStyle: {width: 15, color: [[1, AC_LINE]]}},
            pointer: {show: false},
            axisTick: {show: false},
            splitLine: {show: false},
            axisLabel: {show: false},
            anchor: {show: false},
            title: {show: false},
            detail: {
                offsetCenter: [0, '-35%'],
                fontSize: 20,
                fontWeight: 700,
                color: AC_INK,
                fontFamily: FONT,
                formatter: (v: number) => `${Math.round(v)}%`,
            },
            data: [{value: Math.round(pct * 100)}],
        }],
    };
}

function initGauge(echart: EChartContextType, root: HTMLElement, data: any[]): EChartsType {
    const chart = echart.init(root, 'mithra', {renderer: 'svg', width: 150, height: 100});
    chart.setOption(buildGaugeOption(data[0] as GaugeData));
    return chart;
}

function updateGauge(chart: EChartsType, data: any[]) {
    chart.setOption(buildGaugeOption(data[0] as GaugeData), {notMerge: true});
}

const Gauge: React.FC<{label: string; value: number; meta: number; fmt: (v: number) => string}> =
    ({label, value, meta, fmt}) => {
        const pct = meta > 0 ? Math.min(1, value / meta) : 0;
        return (
            <div className="sit-gauge">
                <div className="sit-gauge-lbl">{label}</div>
                <MithraEchart initChart={initGauge} updateChart={updateGauge} data={[{pct}]}/>
                <div className="sit-gauge-val">{fmt(value)}</div>
                <div className="sit-gauge-meta">{dash('nexoTarget')}: {fmt(meta)}</div>
            </div>
        );
    };

// ── Savings waterfall: stage build-up → total → (delta → target) ──

type WaterfallData = {steps: BarRow[]; total: number; target: number; fmt: (v: number) => string};

function buildWaterfallOption({steps, total, target, fmt}: WaterfallData) {
    type WKind = 'step' | 'total' | 'delta' | 'target';
    const rows: {label: string; value: number; kind: WKind}[] = [
        ...steps.map(s => ({label: s.label, value: s.value, kind: 'step' as WKind})),
        {label: dash('total'), value: total, kind: 'total' as WKind},
    ];
    if (target > 0) rows.push(
        {label: dash('deltaToTarget'), value: Math.abs(total - target), kind: 'delta' as WKind},
        {label: dash('target'), value: target, kind: 'target' as WKind},
    );

    // Each bar is drawn as an invisible `base` segment stacked under a visible
    // `value` segment, the standard ECharts "floating bar" waterfall recipe.
    let run = 0;
    const base: number[] = [];
    const height: number[] = [];
    const color: string[] = [];
    rows.forEach(r => {
        let b: number, t: number, c: string;
        if (r.kind === 'step') {
            b = run;
            t = run + r.value;
            run = t;
            c = AC_NAVY;
        } else if (r.kind === 'total') {
            b = 0;
            t = r.value;
            c = C_TOTAL;
        } else if (r.kind === 'delta') {
            b = Math.min(total, target);
            t = Math.max(total, target);
            c = C_DELTA;
        } else {
            b = 0;
            t = r.value;
            c = AC_ACCENT;
        }
        base.push(b);
        height.push(Math.max(0, t - b));
        color.push(c);
    });

    // Dashed connector between consecutive step bars, at the running-total height.
    const connectors: any[] = [];
    for (let i = 0; i < rows.length - 1; i++) {
        if (rows[i].kind === 'step' && rows[i + 1].kind === 'step') {
            const y = base[i] + height[i];
            connectors.push([{coord: [i, y]}, {coord: [i + 1, y]}]);
        }
    }

    return {
        tooltip: {
            trigger: 'item',
            formatter: (p: any) => `${rows[p.dataIndex].label}: ${fmt(rows[p.dataIndex].value)}`,
        },
        grid: {left: 8, right: 10, top: 30, bottom: 40, containLabel: true},
        xAxis: {
            type: 'category',
            data: rows.map(r => r.label),
            axisLine: {lineStyle: {color: C_AXIS}},
            axisTick: {show: false},
            axisLabel: {
                interval: 0,
                width: 68,
                overflow: 'break',
                lineHeight: 14,
                color: AC_INK_2,
                fontSize: 12,
                fontFamily: FONT,
            },
        },
        yAxis: {type: 'value', show: false},
        series: [
            {
                name: 'base', type: 'bar', stack: 'wf',
                data: base,
                itemStyle: {color: 'transparent'},
                silent: true,
                tooltip: {show: false},
            },
            {
                name: 'value', type: 'bar', stack: 'wf',
                data: height.map((v, i) => ({value: v, itemStyle: {color: color[i], borderRadius: 2}})),
                barMaxWidth: 46,
                label: {
                    show: true,
                    position: 'top',
                    color: AC_INK,
                    fontWeight: 600,
                    fontSize: 12,
                    fontFamily: FONT,
                    formatter: (p: any) => fmt(rows[p.dataIndex].value),
                },
                markLine: {
                    silent: true,
                    symbol: 'none',
                    lineStyle: {color: C_AXIS, type: 'dashed'},
                    label: {show: false},
                    data: connectors,
                },
            },
        ],
    };
}

function initWaterfall(echart: EChartContextType, root: HTMLElement, data: any[]): EChartsType {
    const chart = echart.init(root, 'mithra', {renderer: 'svg', height: 250});
    chart.setOption(buildWaterfallOption(data[0] as WaterfallData));
    return chart;
}

function updateWaterfall(chart: EChartsType, data: any[]) {
    chart.setOption(buildWaterfallOption(data[0] as WaterfallData), {notMerge: true});
}

const Waterfall: React.FC<WaterfallData> = (props) =>
    <MithraEchart initChart={initWaterfall} updateChart={updateWaterfall} data={[props]}/>;

export const IndicatorsDashboard: React.FC = observer(() => {
    const {ccStore, fbStore} = useSitStores();
    const {p} = useStores();

    // Indicators live on fbStore so this page and the header KPI pills share one
    // fetch and both react to the Financial filter bar. Only load if empty here —
    // filter changes drive the reload through fbStore's setters.
    useEffect(() => {
        ccStore.loadInitialData();
        fbStore.reloadData(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Keep the previous data on screen while reloading; overlay a spinner.
    const data = fbStore.indicators;
    const loading = fbStore.nexoIndicatorsRM.busy;

    const mappedTarget = n(data?.mapped_target);
    const unlocked = n(data?.unlocked);
    const captured = n(data?.captured);

    // Target achievement gauges reuse the capture funnel's own figures — % of
    // the NExO target (mapped_target) reached at the unlocked / captured stage.
    const gaugeAbbr = findAbbreviationOfGroup([1, mappedTarget, unlocked, captured]);
    const fmtGauge = (v: number) => _toCurrencyWithP(v, p.currencyFormat, p.currencySymbol, undefined, 3, gaugeAbbr)[0];

    // Capture funnel + workstream bars each pick their own abbreviation per row
    // (matches CurrencyComponent's default, no shared group scale).
    const fmtEach = (v: number) => _toCurrencyWithP(v, p.currencyFormat, p.currencySymbol, undefined, 3)[0];

    // Waterfall build-up = savings per stage gate (N4→N0). Target = the portfolio
    // target (sum of per-year Target records).
    const gates = [...(data?.by_gate ?? [])].sort((a, b) => a.doi_status - b.doi_status);
    const wfSteps = gates.map(g => ({label: `N${5 - g.doi_status}`, value: n(g.total)}));
    const wfTotal = wfSteps.reduce((s, r) => s + r.value, 0);
    const wfTarget = (data?.by_year ?? []).reduce((s, g) => s + n(g.target), 0);
    const wfAbbr = findAbbreviationOfGroup([wfTotal, wfTarget, ...wfSteps.map(s => s.value)]);
    const fmtMoney = (v: number) => _toCurrencyWithP(v, p.currencyFormat, p.currencySymbol, undefined, 3, wfAbbr)[0];

    // By year: actual saving vs target per year (target comes from the Target model).
    const yearRows = (data?.by_year ?? []).map(g => ({year: g.year, total: n(g.total), target: n(g.target)}));
    const yearAbbr = findAbbreviationOfGroup([1, ...yearRows.flatMap(r => [r.total, r.target])]);
    const fmtYear = (v: number) => _toCurrencyWithP(v, p.currencyFormat, p.currencySymbol, undefined, 3, yearAbbr)[0];

    return (
        <div className="indicadores-dashboard sit-list">
            <FinancialFilters/>

            <div className={'sit-chart-area' + (loading ? ' is-loading' : '')}>
                {loading && <div className="sit-chart-loading"><CircularProgress size={28}/></div>}
                <div className="sit-chart-masonry">
                    <ChartCard title={dash('captureFunnel')} sub={dash('captureFunnelSub')} span>
                        <Funnel rows={[
                            {label: dash('mappedTarget'), value: mappedTarget},
                            {label: dash('unlocked'), value: unlocked},
                            {label: dash('captured'), value: captured},
                        ]} fmt={fmtEach}/>
                    </ChartCard>

                    <ChartCard title={dash('byGate')} sub={dash('byGateSub')}>
                        <StageGateBars rows={(data?.by_gate ?? []).map(g => ({
                            n: `N${5 - g.doi_status}`,
                            label: stageGateLabel(g.doi_status).replace(/^N\d+\s*[·-]\s*/, ''),
                            value: n(g.count),
                        }))}/>
                    </ChartCard>

                    <ChartCard title={dash('byWorkstream')} sub={dash('byWorkstreamSub')}>
                        <MoneyBars rows={(data?.by_workstream ?? []).map(g => ({
                            label: workstreamLabel(g.workstream),
                            value: n(g.total)
                        }))} fmt={fmtEach}/>
                    </ChartCard>

                    <ChartCard title={dash('byComplexity')} sub={dash('byComplexitySub')}>
                        <ComplexityDonut rows={(data?.by_farol ?? []).map(g => ({
                            label: g.complexity_tier != null ? complexityTierLabel(g.complexity_tier) : dash('notSet'),
                            value: n(g.count),
                            color: (g.complexity_tier != null && TIER_COLORS[g.complexity_tier]) || C_DELTA,
                        }))}/>
                    </ChartCard>

                    <ChartCard title={dash('byYear')} sub={dash('byYearSub')}
                               legend={[
                                   {color: C_RED, label: dash('target')},
                                   {color: 'var(--ac-navy)', label: dash('total')},
                               ]}>
                        <YearBars rows={yearRows} fmt={fmtYear}/>
                    </ChartCard>

                    <ChartCard title={dash('savingsWaterfall')} sub={dash('savingsWaterfallSub')}
                               legend={[
                                   {color: 'var(--ac-navy)', label: dash('stageBuildup')},
                                   {color: C_TOTAL, label: dash('total')},
                                   ...(wfTarget > 0 ? [
                                       {color: C_DELTA, label: dash('deltaToTarget')},
                                       {color: 'var(--ac-accent)', label: dash('target')},
                                   ] : []),
                               ]}>
                        <Waterfall steps={wfSteps} total={wfTotal} target={wfTarget} fmt={fmtMoney}/>
                    </ChartCard>

                    <ChartCard title={dash('targetAchievement')} sub={dash('targetAchievementSub')}>
                        <div className="sit-gauges vert">
                            <Gauge label={dash('unlockedEfficiencies')} value={unlocked} meta={mappedTarget}
                                   fmt={fmtGauge}/>
                            <Gauge label={dash('capturedEfficiencies')} value={captured} meta={mappedTarget}
                                   fmt={fmtGauge}/>
                        </div>
                    </ChartCard>
                </div>
            </div>
        </div>
    );
});
